import { Navigate, useParams } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { checkoutService } from '@/services/checkout.service'
import { fetchTrackingConfig, initPixels, trackEvent, trackUmami } from '@/lib/tracking'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle, ArrowRight, Mail } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function SuccessPage() {
  const { orderId } = useParams<{ orderId: string }>()

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => checkoutService.getOrder(orderId!),
    enabled: !!orderId,
    refetchInterval: (query) => query.state.data?.payment_status === 'paid' && !query.state.data?.fulfilled ? 5000 : false,
  })

  const purchaseTrackedRef = useRef(false)
  useEffect(() => {
    if (!order || order.payment_status !== 'paid' || purchaseTrackedRef.current) return
    purchaseTrackedRef.current = true
    fetchTrackingConfig().then((config) => {
      initPixels(config)
      trackEvent('Purchase', {
        value: order.final_amount,
        currency: 'BRL',
        content_name: (order as any).product_name || 'SLAC Product',
        transaction_id: order.order_number,
      })
    }).catch(() => {})

    const methodLabels: Record<string, string> = {
      pix: 'PIX',
      boleto: 'Boleto',
      credit_card: 'Cartao de Credito',
    }
    trackUmami('Compra Efetuada', {
      produto: (order as any).product_name || 'SLAC Product',
      valor: order.final_amount,
      metodo: methodLabels[order.payment_method] || order.payment_method,
      pedido: order.order_number,
    })
  }, [order?.id, order?.payment_status])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Pedido não encontrado</CardTitle>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (order.payment_status !== 'paid') {
    return <Navigate to={`/processing/${order.id}`} replace />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 p-4 py-12">
      <div className="mx-auto max-w-2xl">
        {/* Success Icon */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
            <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-green-900 dark:text-green-100">
            Pagamento Confirmado!
          </h1>
          <p className="mt-2 text-muted-foreground">
            Seu pedido foi processado com sucesso
          </p>
        </div>

        {/* Order Details */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhes do Pedido</CardTitle>
            <CardDescription>Número: {order.order_number}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Status:</span>
                <span className="font-semibold text-green-600">Pago</span>
              </div>

              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Data:</span>
                <span>
                  {format(new Date(order.created_at), "dd/MM/yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </span>
              </div>

              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Método:</span>
                <span className="capitalize">
                  {order.payment_method === 'pix' && 'PIX'}
                  {order.payment_method === 'boleto' && 'Boleto'}
                  {order.payment_method === 'credit_card' && 'Cartão de Crédito'}
                </span>
              </div>

              <div className="flex justify-between pt-2">
                <span className="text-lg font-semibold">Total Pago:</span>
                <span className="text-2xl font-bold text-primary">
                  {formatCurrency(order.final_amount)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Próximos Passos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                1
              </div>
              <p className="text-sm">
                Enviamos um e-mail de confirmação com todos os detalhes do seu pedido
              </p>
            </div>

            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                2
              </div>
              <p className="text-sm">
                {order.fulfilled ? 'Seu acesso está liberado. Entre com o e-mail usado na compra.' : 'Estamos liberando seu acesso. Você não precisa pagar novamente.'}
              </p>
            </div>

            <div className="flex gap-3">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                3
              </div>
              <p className="text-sm">
                Em caso de dúvidas, entre em contato com nosso suporte
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button size="lg" asChild>
            <a href={order.access_url || import.meta.env.VITE_PANEL_URL || 'https://panel.slac.com.br/login/email'}>
              Acessar Área do Aluno
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}
